import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, KeyRound, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const Btn = ({ loading, children, testid, ...p }) => (
  <button data-testid={testid} disabled={loading} {...p} className="mt-6 w-full flex items-center justify-center gap-2 rounded-2xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold text-base py-3.5 transition-colors disabled:opacity-60">
    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}{children}
  </button>
);
const F = ({ label, children }) => <div className="mt-4"><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">{label}</Label>{children}</div>;

function PasswordFlow({ onDone }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState("");
  const [pw, setPw] = useState({ a: "", b: "" });
  const [loading, setLoading] = useState(false);
  const run = async (fn) => { setLoading(true); try { await fn(); } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); } };
  const send = (e) => { e.preventDefault(); run(async () => { const { data } = await api.post("/auth/recover/request", { email }); toast.success(`Enviamos un código a ${data.email_mask}. Vence en ${data.minutos} min.`); setStep(2); }); };
  const verify = (e) => { e.preventDefault(); run(async () => { const { data } = await api.post("/auth/recover/verify", { email, code }); setToken(data.reset_token); setStep(3); }); };
  const reset = (e) => {
    e.preventDefault();
    if (pw.a.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres."); return; }
    if (pw.a !== pw.b) { toast.error("Las contraseñas no coinciden."); return; }
    run(async () => { await api.post("/auth/recover/reset", { reset_token: token, new_password: pw.a }); toast.success("¡Listo! Contraseña actualizada. Inicia sesión con tu nueva contraseña."); onDone(); });
  };
  if (step === 1) return (
    <form onSubmit={send} data-testid="recover-step-email">
      <p className="text-sm text-[#6B7688]">Escribe el correo con el que te registraste la primera vez. Te enviaremos un código de 6 dígitos.</p>
      <F label="Correo de registro"><Input data-testid="recover-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tucorreo@ejemplo.com" /></F>
      <Btn testid="recover-send-code" loading={loading} type="submit">Enviar código <ArrowRight className="w-5 h-5" /></Btn>
    </form>
  );
  if (step === 2) return (
    <form onSubmit={verify} data-testid="recover-step-code">
      <p className="text-sm text-[#6B7688]">Revisa tu correo (también la carpeta de spam). El código vence en 10 minutos y tienes 5 intentos.</p>
      <F label="Código de 6 dígitos"><Input data-testid="recover-code" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="text-center text-2xl tracking-[0.5em] font-extrabold h-14" /></F>
      <Btn testid="recover-verify-code" loading={loading} type="submit">Verificar código</Btn>
      <button type="button" data-testid="recover-resend" onClick={send} className="mt-3 w-full text-sm font-bold text-[#3B7DF6]">Enviar un código nuevo</button>
    </form>
  );
  return (
    <form onSubmit={reset} data-testid="recover-step-password">
      <p className="text-sm text-[#6B7688]">Crea tu nueva contraseña. Por seguridad, se cerrarán las sesiones abiertas en otros dispositivos.</p>
      <F label="Nueva contraseña"><Input data-testid="recover-new-password" type="password" value={pw.a} onChange={(e) => setPw((p) => ({ ...p, a: e.target.value }))} placeholder="Mínimo 6 caracteres" /></F>
      <F label="Repite la contraseña"><Input data-testid="recover-new-password2" type="password" value={pw.b} onChange={(e) => setPw((p) => ({ ...p, b: e.target.value }))} /></F>
      <Btn testid="recover-save-password" loading={loading} type="submit">Guardar nueva contraseña</Btn>
    </form>
  );
}

function EmailFlow({ onDone }) {
  const [f, setF] = useState({ whatsapp: "", nombre: "", referencia: "" });
  const [res, setRes] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const { data } = await api.post("/auth/recover/email", f); setRes(data); }
    catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  if (res) return (
    <div data-testid="recover-email-result" className="rounded-2xl bg-[#E2F6EB] ring-1 ring-[#22A860]/30 p-5 text-sm text-[#1B6B40]">
      <p className="font-extrabold">¡Te encontramos!</p>
      <p className="mt-1">Tu correo de acceso es <b data-testid="recover-email-mask">{res.email_mask}</b>. {res.enviado === false ? "No pudimos enviarte el correo completo en este momento; si no lo reconoces, contacta a tu coordinador Supera." : "Te enviamos el correo completo a ese mismo buzón."}</p>
      <button data-testid="recover-email-login" onClick={onDone} className="mt-4 w-full rounded-xl bg-[#0A1C30] text-white font-bold py-2.5">Ir a iniciar sesión</button>
    </div>
  );
  return (
    <form onSubmit={submit} data-testid="recover-email-form">
      <p className="text-sm text-[#6B7688]">Escribe los datos tal como los registraste. Si coinciden, te mostraremos tu correo parcialmente oculto.</p>
      <F label="WhatsApp registrado"><Input data-testid="recover-wa" required value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+52 ..." /></F>
      <F label="Nombre completo"><Input data-testid="recover-nombre" required value={f.nombre} onChange={(e) => set("nombre", e.target.value)} placeholder="Nombre y apellidos" /></F>
      <F label="Referencia Supera"><Input data-testid="recover-ref" required value={f.referencia} onChange={(e) => set("referencia", e.target.value)} placeholder="Ej. SUPERA-KIDS-001" /></F>
      <Btn testid="recover-email-submit" loading={loading} type="submit">Buscar mi correo</Btn>
    </form>
  );
}

export default function Recuperar() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("password");
  const tabs = [["password", "Olvidé mi contraseña", KeyRound], ["email", "Olvidé mi correo", Mail]];
  return (
    <div className="min-h-screen bg-navy flex flex-col">
      <div className="max-w-md w-full mx-auto px-5 sm:px-6 flex-1 flex flex-col justify-center py-12">
        <button data-testid="recover-back" onClick={() => navigate("/login")} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold transition-colors mb-8"><ArrowLeft className="w-4 h-4" /> Volver a iniciar sesión</button>
        <p className="text-xs font-extrabold tracking-[0.25em] text-gold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> RECUPERACIÓN SEGURA</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-white">Recupera tu <span className="font-serif-it text-gold">acceso</span></h1>
        <div className="mt-7 bg-white rounded-[28px] p-6 sm:p-8 shadow-2xl">
          <div className="grid grid-cols-2 gap-2 rounded-2xl bg-[#F4F6F9] p-1 mb-5">
            {tabs.map(([k, l, I]) => (
              <button key={k} data-testid={`recover-tab-${k}`} onClick={() => setTab(k)} className={`flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs sm:text-sm font-bold transition-colors ${tab === k ? "bg-[#0A1C30] text-white" : "text-[#0A1C30]"}`}><I className="w-4 h-4" />{l}</button>
            ))}
          </div>
          {tab === "password" ? <PasswordFlow onDone={() => navigate("/login")} /> : <EmailFlow onDone={() => navigate("/login")} />}
        </div>
      </div>
    </div>
  );
}
